/**
 * useViewerShortcuts — Iter 36.5 Viewer Architecture Hardening.
 *
 * Owns the keyboard shortcut handler for SheetViewer.
 *
 *   f/F   field mode toggle
 *   p/P   add-pin toggle
 *   s/S   sidebar toggle
 *   g/G   gps panel toggle
 *   m/M   measure toggle
 *   z/Z   undo measurement point (area mode only)
 *   c/C   crop toggle
 *   n/N   markup toggle
 *   j/J/# sheet-jump overlay
 *   [/]   prev/next sheet (also ←/→ when NOT in field mode — swipe owns
 *         the gesture there)
 *   +/=   zoom in
 *   -/_   zoom out
 *   0     fit
 *   Esc   cancel whatever is open
 *
 * Pure extraction — semantics identical to the previous inline keydown
 * effect in SheetViewer.jsx.
 */
import { useEffect } from "react";
import { dispatchCanvasCmd } from "@/components/SheetCanvas";


export function useViewerShortcuts({
  sheetId,
  sheet,
  projectSheets,
  fieldMode,
  addMode,
  awaitingAnchorTap,
  measureMode,
  toolMode,
  measurePtsLength,
  cropMode,
  markupMode,
  navigate,
  // actions
  toggleFieldMode,
  toggleSidebar,
  setAddMode,
  setNewPinPos,
  setTemplate,
  setGpsPanelOpen,
  startMeasure,
  closeMeasure,
  undoMeasurePoint,
  openCrop,
  closeCrop,
  openMarkup,
  closeMarkup,
  setJumpOpen,
  setAwaitingAnchorTap,
  setAnchorTapPos,
}) {
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.ctrlKey || e.metaKey) return;
      switch (e.key) {
        case "f": case "F": e.preventDefault(); toggleFieldMode(); break;
        case "p": case "P":
          e.preventDefault();
          setAddMode((v) => !v); setNewPinPos(null); setTemplate(null);
          break;
        case "s": case "S": e.preventDefault(); toggleSidebar(); break;
        case "g": case "G":
          e.preventDefault(); setGpsPanelOpen((v) => !v); break;
        case "m": case "M":
          e.preventDefault();
          if (measureMode === "off") startMeasure();
          else closeMeasure();
          break;
        case "z": case "Z":
          if (measureMode === "measure" && toolMode === "area" && measurePtsLength > 0) {
            e.preventDefault();
            undoMeasurePoint();
          }
          break;
        case "c": case "C":
          e.preventDefault();
          if (cropMode) closeCrop();
          else openCrop();
          break;
        case "n": case "N":
          e.preventDefault();
          if (markupMode) closeMarkup();
          else openMarkup();
          break;
        case "#": case "j": case "J":
          e.preventDefault(); setJumpOpen(true); break;
        case "[":
        case "ArrowLeft": {
          if (e.key === "ArrowLeft" && fieldMode) break;
          if (!projectSheets.length || !sheet) break;
          e.preventDefault();
          const i = projectSheets.findIndex((s) => s.id === sheet.id);
          if (i > 0) navigate(`/sheet/${projectSheets[i - 1].id}`);
          break;
        }
        case "]":
        case "ArrowRight": {
          if (e.key === "ArrowRight" && fieldMode) break;
          if (!projectSheets.length || !sheet) break;
          e.preventDefault();
          const i = projectSheets.findIndex((s) => s.id === sheet.id);
          if (i >= 0 && i < projectSheets.length - 1) navigate(`/sheet/${projectSheets[i + 1].id}`);
          break;
        }
        case "+": case "=":
          e.preventDefault(); dispatchCanvasCmd(sheetId, "zoomIn"); break;
        case "-": case "_":
          e.preventDefault(); dispatchCanvasCmd(sheetId, "zoomOut"); break;
        case "0":
          e.preventDefault(); dispatchCanvasCmd(sheetId, "fit"); break;
        case "Escape":
          if (addMode) { setAddMode(false); setNewPinPos(null); setTemplate(null); }
          if (awaitingAnchorTap) { setAwaitingAnchorTap(false); setAnchorTapPos(null); }
          if (measureMode !== "off") closeMeasure();
          if (cropMode) closeCrop();
          if (markupMode) closeMarkup();
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    sheetId, sheet, projectSheets, fieldMode,
    addMode, awaitingAnchorTap, measureMode, toolMode, measurePtsLength,
    cropMode, markupMode, navigate,
    toggleFieldMode, toggleSidebar, setAddMode, setNewPinPos, setTemplate,
    setGpsPanelOpen, startMeasure, closeMeasure, undoMeasurePoint,
    openCrop, closeCrop, openMarkup, closeMarkup, setJumpOpen,
    setAwaitingAnchorTap, setAnchorTapPos,
  ]);
}
